/**
 * Alocação · RBS (migrada do Streamlit; equivalência em backend/tests_paginas/test_rbs.py).
 *
 * O espectro leva ~100 s (calibração do Fibery + otimização) e roda em segundo plano no
 * servidor, como o Factor Investing: a tela pergunta a cada 3 s até ficar pronto. Status
 * e overrides só valem ao clicar em "Calcular espectro" (cada mudança refaz o cálculo);
 * o perfil do detalhe muda na hora, sobre o resultado guardado.
 */
import {
  Alert,
  Button,
  Group,
  Loader,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Tabs,
  Text,
} from '@mantine/core';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { ErroApi, apiGet, montarUrl, type Envelope } from '../../api/cliente';
import { AvisosApi } from '../../components/AvisosApi';
import { Cronometro } from '../../components/Cronometro';
import { BarraFiltros, GrupoFiltros, LARGURA } from '../../components/Filtros';
import { Grafico, type GraficoApi } from '../../components/Grafico';
import { Kpi, type KpiApi } from '../../components/Kpi';
import { CabecalhoPagina, Pagina, Secao } from '../../components/Layout';
import { TabelaDados, type TabelaApi } from '../../components/TabelaDados';
import { formatarDataHora } from '../../lib/formatos';
import { useFiltrosUrl } from '../../lib/url-state';

const CAMINHO = '/carteiras-administradas/alocacao-rbs';
const STATUS = ['Rascunho', 'Em Comitê', 'Aprovada', 'Substituída'];

export const PADROES = {
  status: 'Aprovada',
  overrides: false as boolean,
  sigma_min: 2.1,
  sigma_max: 12,
  n_perfis: 10,
  limite_peso: 0.005,
  perfil: 0,
};
type Filtros = typeof PADROES;

interface Resultado {
  cabecalho: KpiApi[];
  avisos: string[];
  visao: { kpis: KpiApi[]; classes: TabelaApi; rc: TabelaApi };
  espectro: {
    pesos_classe: GraficoApi;
    pesos_bucket: GraficoApi;
    rc_classe: GraficoApi;
    rc_bucket: GraficoApi;
    sigma: GraficoApi;
    tabela: TabelaApi;
  };
  detalhe: {
    perfis: { valor: number; rotulo: string }[];
    perfil: number;
    kpis: KpiApi[];
    composicao: TabelaApi;
    donuts: { titulo: string; grafico?: GraficoApi; aviso?: string }[];
    rc_bucket: GraficoApi;
    rc_classe: GraficoApi;
  };
  inputs: {
    vols: TabelaApi;
    grafico_vols: GraficoApi;
    correlacao: GraficoApi;
    macro: GraficoApi;
    intra: GraficoApi[];
  };
  hrp: { tabela: TabelaApi; grafico: GraficoApi; macro: TabelaApi; donut: GraficoApi; vol: KpiApi };
}
interface Estado {
  status: 'rodando' | 'pronto' | 'erro';
  iniciado_em: string;
  mensagem?: string;
  resultado?: Resultado;
}

function Formulario({
  inicial,
  carregando,
  aoCalcular,
}: {
  inicial: Filtros;
  carregando: boolean;
  aoCalcular: (f: Partial<Filtros>) => void;
}) {
  const [f, setF] = useState(inicial);
  const mudar = (m: Partial<Filtros>) => setF((a) => ({ ...a, ...m }));
  const num = (v: string | number, padrao: number) => (v === '' ? padrao : Number(v));
  return (
    <BarraFiltros carregando={carregando}>
      <GrupoFiltros titulo="Calibração">
        <Select
          label="Status"
          data={STATUS}
          value={f.status}
          onChange={(status) => status && mudar({ status })}
          w={LARGURA.selecao - 60}
        />
      </GrupoFiltros>
      <GrupoFiltros titulo="Overrides">
        <Switch
          label="Habilitar"
          checked={f.overrides}
          onChange={(e) => mudar({ overrides: e.currentTarget.checked })}
          mt={30}
        />
        <NumberInput
          label="σ mínimo (%)"
          disabled={!f.overrides}
          value={f.sigma_min}
          onChange={(v) => mudar({ sigma_min: num(v, 2.1) })}
          min={0.1}
          max={50}
          step={0.1}
          decimalScale={2}
          decimalSeparator=","
          w={LARGURA.numero}
        />
        <NumberInput
          label="σ máximo (%)"
          disabled={!f.overrides}
          value={f.sigma_max}
          onChange={(v) => mudar({ sigma_max: num(v, 12) })}
          min={0.1}
          max={50}
          step={0.1}
          decimalScale={2}
          decimalSeparator=","
          w={LARGURA.numero}
        />
        <NumberInput
          label="Nº de perfis"
          disabled={!f.overrides}
          value={f.n_perfis}
          onChange={(v) => mudar({ n_perfis: num(v, 10) })}
          min={2}
          max={100}
          w={LARGURA.numero}
        />
        <NumberInput
          label="Limite mínimo de peso"
          disabled={!f.overrides}
          value={f.limite_peso}
          onChange={(v) => mudar({ limite_peso: num(v, 0.005) })}
          min={0}
          max={0.05}
          step={0.001}
          decimalScale={3}
          decimalSeparator=","
          w={LARGURA.numero + 20}
        />
      </GrupoFiltros>
      <GrupoFiltros titulo="Calcular">
        <Button mt={25} color="dourado" onClick={() => aoCalcular({ ...f, perfil: 0 })}>
          Calcular espectro
        </Button>
      </GrupoFiltros>
    </BarraFiltros>
  );
}

function Grade({ children }: { children: React.ReactNode }) {
  return (
    <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="lg">
      {children}
    </SimpleGrid>
  );
}

export function AlocacaoRbs() {
  const [filtros, definir] = useFiltrosUrl(PADROES);
  const parametros = {
    status: filtros.status,
    overrides: filtros.overrides,
    ...(filtros.overrides
      ? {
          sigma_min: filtros.sigma_min,
          sigma_max: filtros.sigma_max,
          n_perfis: filtros.n_perfis,
          limite_peso: filtros.limite_peso,
        }
      : {}),
  };
  const consulta = useQuery({
    queryKey: ['carteiras.rbs', parametros, filtros.perfil],
    queryFn: ({ signal }) =>
      apiGet<Envelope<Estado>>(
        CAMINHO,
        { ...parametros, perfil: filtros.perfil || undefined },
        signal,
      ),
    placeholderData: keepPreviousData,
    refetchIntervalInBackground: true,
    refetchInterval: (q) => (q.state.data?.data.status === 'rodando' ? 3000 : false),
  });
  const estado = consulta.data?.data;
  const r = estado?.status === 'pronto' ? estado.resultado : undefined;
  const chaveFormulario = JSON.stringify(parametros);

  return (
    <Pagina>
      <CabecalhoPagina
        titulo="Alocação · RBS"
        descricao="Modelo de alocação por Risk Budgeting Spectrum: pesos e contribuições de risco por perfil, a partir da calibração do Comitê."
        fonte="Fibery (calibração e classes de ativo) · persevera_tools"
        acoes={
          r && (
            <Button
              component="a"
              variant="light"
              color="dourado"
              href={montarUrl(`${CAMINHO}/excel`, parametros)}
            >
              Baixar Excel
            </Button>
          )
        }
      />
      <Formulario
        key={chaveFormulario}
        inicial={filtros}
        carregando={consulta.isFetching && estado?.status !== 'rodando'}
        aoCalcular={(f) => definir(f)}
      />
      <AvisosApi avisos={[consulta.data?.meta.avisos]} />
      {consulta.isError && (
        <Alert color="red">
          {consulta.error instanceof ErroApi
            ? consulta.error.message
            : 'Não foi possível calcular o espectro.'}
        </Alert>
      )}
      {!estado && consulta.isPending && <Loader />}
      {estado?.status === 'rodando' && (
        <Alert color="blue" icon={<Loader size="sm" />} title="Calculando o espectro">
          Iniciado em {formatarDataHora(estado.iniciado_em)} (há{' '}
          <Cronometro desde={estado.iniciado_em} />
          ). Leva cerca de 2 minutos; o resultado aparece aqui sozinho.
        </Alert>
      )}
      {estado?.status === 'erro' && (
        <Alert color="red" title="Não foi possível calcular o espectro">
          {estado.mensagem}
        </Alert>
      )}
      {r && (
        <>
          <SimpleGrid cols={{ base: 2, sm: 3, lg: 5 }}>
            {r.cabecalho.map((k) => (
              <Kpi key={k.rotulo} kpi={k} />
            ))}
          </SimpleGrid>
          <AvisosApi avisos={[r.avisos]} />
          <Tabs defaultValue="visao" keepMounted={false} color="dourado">
            <Tabs.List>
              <Tabs.Tab value="visao">Visão geral</Tabs.Tab>
              <Tabs.Tab value="espectro">Espectro de alocação</Tabs.Tab>
              <Tabs.Tab value="perfil">Detalhe do perfil</Tabs.Tab>
              <Tabs.Tab value="inputs">Inputs do modelo</Tabs.Tab>
              <Tabs.Tab value="hrp">Diagnóstico HRP</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="visao" pt="md">
              <Stack gap="lg">
                <SimpleGrid cols={{ base: 2, lg: 4 }}>
                  {r.visao.kpis.map((k) => (
                    <Kpi key={k.rotulo} kpi={k} />
                  ))}
                </SimpleGrid>
                <Secao
                  titulo="Configuração das classes"
                  legenda="Volatilidade e limites de peso e de contribuição de risco de cada classe."
                >
                  <TabelaDados tabela={r.visao.classes} alturaAutomatica />
                </Secao>
                <Secao
                  titulo="RC-targets por bucket"
                  legenda="RC P1 e RC PN são os orçamentos de risco nos extremos do espectro. A curvatura γ controla a transição: γ=1 interpola linearmente; γ<1 transita cedo (côncavo); γ>1, tarde (convexo)."
                >
                  <TabelaDados tabela={r.visao.rc} alturaAutomatica />
                </Secao>
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="espectro" pt="md">
              <Stack gap="lg">
                <Grade>
                  <Secao titulo="Pesos por classe">
                    <Grafico grafico={r.espectro.pesos_classe} />
                  </Secao>
                  <Secao titulo="Pesos por bucket">
                    <Grafico grafico={r.espectro.pesos_bucket} />
                  </Secao>
                  <Secao titulo="Contribuição de risco por classe">
                    <Grafico grafico={r.espectro.rc_classe} />
                  </Secao>
                  <Secao titulo="Contribuição de risco por bucket">
                    <Grafico grafico={r.espectro.rc_bucket} />
                  </Secao>
                </Grade>
                <Secao titulo="σ alvo vs σ realizado">
                  <Grafico grafico={r.espectro.sigma} />
                </Secao>
                <Secao titulo="Pesos por perfil (%)">
                  <TabelaDados tabela={r.espectro.tabela} alturaAutomatica />
                </Secao>
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="perfil" pt="md">
              <Stack gap="lg">
                <Group align="flex-end">
                  <Select
                    label="Perfil"
                    data={r.detalhe.perfis.map((p) => ({
                      value: String(p.valor),
                      label: p.rotulo,
                    }))}
                    value={String(r.detalhe.perfil)}
                    onChange={(v) => v && definir({ perfil: Number(v) })}
                    w={LARGURA.selecao}
                  />
                </Group>
                <SimpleGrid cols={{ base: 2, lg: 4 }}>
                  {r.detalhe.kpis.map((k) => (
                    <Kpi key={k.rotulo} kpi={k} />
                  ))}
                </SimpleGrid>
                <Grade>
                  <Secao
                    titulo="Composição por classe"
                    legenda="Δ RC = RC realizado − RC alvo, em pontos percentuais."
                  >
                    <TabelaDados tabela={r.detalhe.composicao} alturaAutomatica />
                  </Secao>
                  <Secao titulo="Distribuição de pesos">
                    <Tabs defaultValue="0" keepMounted={false} color="dourado">
                      <Tabs.List>
                        {r.detalhe.donuts.map((d, i) => (
                          <Tabs.Tab key={d.titulo} value={String(i)}>
                            {d.titulo}
                          </Tabs.Tab>
                        ))}
                      </Tabs.List>
                      {r.detalhe.donuts.map((d, i) => (
                        <Tabs.Panel key={d.titulo} value={String(i)} pt="sm">
                          {d.grafico ? (
                            <Grafico grafico={d.grafico} />
                          ) : (
                            <Text size="sm" c="dimmed">
                              {d.aviso}
                            </Text>
                          )}
                        </Tabs.Panel>
                      ))}
                    </Tabs>
                  </Secao>
                  <Secao titulo="Contribuição de risco · por bucket" legenda="Alvo vs. realizado.">
                    <Grafico grafico={r.detalhe.rc_bucket} />
                  </Secao>
                  <Secao titulo="Contribuição de risco · por classe" legenda="Alvo vs. realizado.">
                    <Grafico grafico={r.detalhe.rc_classe} />
                  </Secao>
                </Grade>
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="inputs" pt="md">
              <Stack gap="lg">
                <Grade>
                  <Secao titulo="Volatilidades por classe">
                    <TabelaDados tabela={r.inputs.vols} alturaAutomatica />
                  </Secao>
                  <Secao titulo="Volatilidade anualizada">
                    <Grafico grafico={r.inputs.grafico_vols} />
                  </Secao>
                </Grade>
                <Secao titulo="Correlação implícita" legenda="Matriz completa entre as classes.">
                  <Grafico grafico={r.inputs.correlacao} />
                </Secao>
                <Secao
                  titulo="Correlações macro e intra-bucket"
                  legenda="Macro: correlação entre portfólios de bucket, derivada da matriz completa com os pesos intra-bucket do HRP. Intra: blocos da matriz completa por bucket."
                >
                  <Stack gap="md">
                    <Grafico grafico={r.inputs.macro} />
                    <SimpleGrid cols={{ base: 1, md: Math.min(3, r.inputs.intra.length) }}>
                      {r.inputs.intra.map((g) => (
                        <Grafico key={g.id} grafico={g} />
                      ))}
                    </SimpleGrid>
                  </Stack>
                </Secao>
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="hrp" pt="md">
              <Stack gap="lg">
                <Text size="sm">
                  Pesos de referência por <b>HRP</b> (Hierarchical Risk Parity) de dois níveis.{' '}
                  <b>Não fazem parte do pipeline de produção</b>: servem só como benchmark
                  diagnóstico para comparar com as carteiras do espectro, geradas por risk budgeting
                  com σ-alvo.
                </Text>
                <Grade>
                  <Secao titulo="Pesos HRP por classe">
                    <Stack>
                      <TabelaDados tabela={r.hrp.tabela} alturaAutomatica />
                      <Grafico grafico={r.hrp.grafico} />
                    </Stack>
                  </Secao>
                  <Secao titulo="Pesos HRP por bucket (agregação macro)">
                    <Stack>
                      <TabelaDados tabela={r.hrp.macro} alturaAutomatica />
                      <Grafico grafico={r.hrp.donut} />
                      <Kpi kpi={r.hrp.vol} />
                    </Stack>
                  </Secao>
                </Grade>
              </Stack>
            </Tabs.Panel>
          </Tabs>
        </>
      )}
    </Pagina>
  );
}
